// src/pages/Playbook.tsx
import React from "react";
import SEOHead from "../components/SEOHead";
import {
  CheckCircle2, GitBranch, Workflow, FileSearch, Sparkles, Target,
  Code, RefreshCw, LineChart, BarChart, AlertTriangle, Shield,
  ClipboardList, BookOpen, Download, Database, Settings, MessageSquare, ArrowRight
} from "lucide-react";
import AnimatedSection from "../components/AnimatedSection";
import PageTransition from "../components/PageTransition";
import GradientHero from "../components/GradientHero";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 }
  })
};

// JSON-LD
const jsonLdPlaybook = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "name": "Playbook QA",
      "url": "https://neoqualis.com/playbook",
      "description": "Référentiel Neoqualis de standards QA : principes, méthodes, modèles et checklists."
    },
    {
      "@type": "CreativeWork",
      "name": "Playbook QA Neoqualis",
      "about": "Quality Assurance, Software Testing, CI/CD, Performance",
      "audience": { "@type": "Audience", "audienceType": "Produit, IT, QA" },
      "creator": { "@type": "Organization", "name": "Neoqualis" }
    }
  ]
};

const principles = [
  "Clarté des exigences & critères d’acceptation testables",
  "Traçabilité bout-en-bout (exigence → scénario → cas → exécution → défaut)",
  "Boucles de feedback courtes, risk-based testing",
  "Couverture utile > quantité de tests",
  "Qualité en continu (shift-left & CI/CD)"
];

const governance = [
  {
    title: "Rôles & RACI",
    desc: "BA/PO (exigences & AC), QA (stratégie & test design), Dev (unitaires & PR checks), DevOps (pipelines), QA Lead (KPIs, risques, go/no-go).",
    icon: Workflow
  },
  {
    title: "Rituels",
    desc: "Revue d’exigences (DoR), triage défauts, revues de campagne, comités risques, rétrospective qualité.",
    icon: GitBranch
  },
  {
    title: "Standards",
    desc: "DoR/DoD, modèles de stratégie de test, matrices de traçabilité, checklists de revue.",
    icon: ClipboardList
  }
];

const traceability = [
  {
    title: "Definition of Ready",
    points: [
      "US claire, objectif métier explicite",
      "AC Gherkin Given/When/Then",
      "Jeux de données identifiés",
      "Dépendances & mocks connus"
    ],
    icon: FileSearch
  },
  {
    title: "Traçabilité",
    points: [
      "Exigence ↔ scénario ↔ cas ↔ exécution ↔ défaut",
      "Xray / Squash TM, statuts & liens normalisés",
      "Couverture par risque & par parcours"
    ],
    icon: LineChart
  },
  {
    title: "QualisAI (option)",
    points: [
      "Analyse de specs: ambiguïtés & incohérences",
      "Génération de scénarios Gherkin",
      "Exports Xray/Squash & passerelles automation"
    ],
    icon: Sparkles
  }
];

const strategy = [
  {
    title: "Pyramide de tests",
    desc: "Unitaires massifs → API (contrats/règles) → UI (parcours critiques) → E2E (rares, stables).",
    icon: Target
  },
  {
    title: "Types & critères de sortie",
    desc: "Smoke, régression, non-régression ciblée, sécurité rapide, performance. Sortie: couverture, défauts ouverts, risques résiduels, santé pipeline.",
    icon: Settings
  }
];

const automation = [
  {
    title: "Standards d’implémentation",
    points: [
      "Sélecteurs stables (data-testid), pas de sleep, attentes conditionnelles",
      "POM/Screenplay, data builders, fixtures",
      "Artefacts (logs, screenshots, vidéos) en CI"
    ],
    icon: Code
  },
  {
    title: "CI/CD & stabilité",
    points: [
      "Batchs parallèles, tags (smoke/reg/critique)",
      "Seuils de qualité, rapports centralisés",
      "Déflakiness: isolation des données, retry contrôlé"
    ],
    icon: RefreshCw
  }
];

const dataEnv = [
  {
    title: "Données",
    desc: "Jeux synthétiques anonymisés, seeds reproductibles, isolation par test, nettoyage fin de test.",
    icon: Database
  },
  {
    title: "Environnements",
    desc: "INT → UAT → PREPROD, parité config, observabilité de base (logs/metrics/traces) pour diagnostiquer rapidement.",
    icon: Shield
  }
];

const performance = [
  {
    title: "Plan de charge",
    desc: "Objectifs (TPS/latence/erreurs), profils (rampe, paliers, endurance), think-time réaliste, corrélation dynamique.",
    icon: BarChart
  },
  {
    title: "Analyse & sorties",
    desc: "KPIs p95, taux d’erreur, saturation CPU/mémoire/IO, goulots par composant. Recos priorisées: quick wins vs tuning infra/app.",
    icon: AlertTriangle
  }
];

const defectsQuality = [
  {
    title: "Cycle de vie & priorisation",
    desc: "New → Triaged → In Progress → Ready for Test → Resolved → Closed. Priorisation = impact × probabilité × détectabilité.",
    icon: ClipboardList
  },
  {
    title: "RCA & qualité",
    desc: "Détection doublons, 5-Whys, tags cause (tech/orga/process). Défauts évitables remontés en rétrospective qualité.",
    icon: Shield
  }
];

const kpis = [
  "Couverture utile (exigences/risques)",
  "Défauts bloquants pré-MEP (tendance)",
  "Flakiness automation (%)",
  "Lead time de recette",
  "Taux de fuite vers la prod",
  "MTTR lié à la qualité"
];

const templates = [
  { tag: "Stratégie de test (1 page)", icon: CheckCircle2 },
  { tag: "Checklist DoR / DoD", icon: CheckCircle2 },
  { tag: "Checklist déflakiness automation", icon: CheckCircle2 },
  { tag: "Squelette plan JMeter", icon: CheckCircle2 },
  { tag: "Sommaire type de rapport d’audit", icon: CheckCircle2 }
];

const Playbook: React.FC = () => {
  return (
    <PageTransition>
      <SEOHead
        title="Playbook QA — Standards, modèles & checklists | Neoqualis"
        description="Le Playbook QA Neoqualis : principes, gouvernance, traçabilité, stratégie, automatisation, performance et KPIs avec modèles prêts à l’emploi."
        canonical="https://neoqualis.com/playbook"
        jsonLd={jsonLdPlaybook}
      />

      <div className="pt-16">
        {/* Hero */}
        <GradientHero
          title="Playbook QA"
          subtitle="Notre référentiel qualité : principes, méthodes, traçabilité, automation, performance et KPIs — avec des modèles actionnables."
          ctas={[
            { label: "Demander le Playbook complet (PDF)", to: "/contact?type=playbook", variant: "primary", iconRight: <Download /> },
            { label: "Découvrir l’Audit QA", to: "/audit", variant: "secondary", iconRight: <ArrowRight /> },
          ]}
        />

        {/* Principes directeurs */}
        <section id="principes" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-10">
              <h2 className="heading">Principes directeurs</h2>
              <p className="subheading max-w-2xl mx-auto">
                Clarté, traçabilité, feedback court et couverture utile.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {principles.map((p, i) => (
                <motion.div
                  key={p}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-secondary mr-3 mt-0.5" />
                    <p className="text-gray-700">{p}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Gouvernance & rôles */}
        <section id="gouvernance" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Gouvernance & rôles</h2>
              <p className="subheading max-w-2xl mx-auto">
                Un cadre clair pour décider vite et bien.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {governance.map(({ title, desc, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Icon className="text-primary w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Exigences, traçabilité & QualisAI */}
        <section id="exigences" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Exigences, traçabilité & QualisAI</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {traceability.map(({ title, points, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-secondary/10">
                        <Icon className="text-secondary w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {points.map((pt: string, idx: number) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-secondary mr-2 mt-0.5" />
                          <span className="text-sm">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-blue-50 border border-blue-200 max-w-4xl mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Extrait Gherkin (exemple)</h3>
                <pre className="text-sm text-gray-800 overflow-x-auto"><code>
{`Feature: Validation d’identité (KYC)
  Scenario: KYC valide
    Given un client avec pièces valides
    When il soumet son dossier
    Then le statut KYC passe à "validé"`}
                </code></pre>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Stratégie de test */}
        <section id="strategie" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Stratégie de test</h2>
              <p className="subheading max-w-2xl mx-auto">
                La bonne couverture au bon niveau.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {strategy.map(({ title, desc, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Icon className="text-primary w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Automatisation & CI/CD */}
        <section id="automation" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Automatisation & CI/CD</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
              {automation.map(({ title, points, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-secondary/10">
                        <Icon className="text-secondary w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {points.map((pt: string, idx: number) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-secondary mr-2 mt-0.5" />
                          <span className="text-sm">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-orange-50 border border-orange-200 max-w-4xl mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Déflakiness (extrait)</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    "data-testid stables ; pas de sleep — préférer des attentes conditionnelles",
                    "Isolation des données par test ; reset d’état",
                    "Retry limité sur actions idempotentes",
                    "Artefacts activés (logs, screenshots, vidéos) en CI"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-secondary mr-2 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Données & environnements */}
        <section id="data-env" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Données & environnements</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {dataEnv.map(({ title, desc, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Icon className="text-primary w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Performance (JMeter) */}
        <section id="performance" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Performance (JMeter)</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {performance.map(({ title, desc, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-secondary/10">
                        <Icon className="text-secondary w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Défauts & qualité */}
        <section id="defauts" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Défauts & qualité</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {defectsQuality.map(({ title, desc, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Icon className="text-primary w-8 h-8" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* KPIs & reporting */}
        <section id="kpis" className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-10">
              <h2 className="heading">KPIs & reporting</h2>
              <p className="subheading max-w-2xl mx-auto">Mesurer ce qui compte vraiment.</p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {kpis.map((k, i) => (
                <motion.div key={k} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full flex items-start">
                    <BarChart className="w-6 h-6 text-secondary mr-3 mt-0.5" />
                    <p className="text-gray-700">{k}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Templates & checklists */}
        <section id="templates" className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Templates & checklists (extraits)</h2>
              <p className="subheading max-w-2xl mx-auto">
                Des modèles prêts à l’emploi pour accélérer et standardiser.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {templates.map(({ tag, icon: Icon }, i) => (
                <motion.div key={tag} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full flex items-center">
                    <div className="p-3 rounded-full bg-secondary/10 mr-3">
                      <Icon className="text-secondary w-6 h-6" />
                    </div>
                    <span className="text-gray-800 font-medium">{tag}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/contact?type=playbook" className="btn-primary inline-flex items-center">
                Télécharger le Playbook complet (PDF — 20 pages)
                <Download className="ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="heading mb-6">Envie d’aller plus loin ?</h2>
                <p className="subheading mb-8">
                  Demandez une démo de QualisAI ou planifions un audit QA pour appliquer le Playbook à votre contexte.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact?type=demo-qualisai" className="btn-secondary inline-flex items-center">
                    Demander une démo QualisAI
                    <MessageSquare className="ml-2" />
                  </Link>
                  <Link to="/audit" className="btn-primary inline-flex items-center">
                    Demander un audit QA (offert – 30 min)
                    <ArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Playbook;
